import { useAuth } from "@elit-hair/elit-tools";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronDown, TbPlus, TbRefresh, TbX } from "react-icons/tb";
import { toast } from "sonner";

import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from "@/components/multi-select";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { SearchBar } from "@/components/ui/search-bar";
import { getTotalPages, PAGINATION } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import {
  useDeleteUser,
  usePermissionGroups,
  useUsers,
} from "@/hooks/queries/user.queries";
import { UserCard } from "@/pages/users/components/user-card";
import { UserFormDialog } from "@/pages/users/components/user-form-dialog";
import { UsersTable } from "@/pages/users/components/users-table";
import {
  DEFAULT_USER_STATUS_FILTER,
  USER_STATUS_FILTER_OPTIONS,
  USERS_PAGE_SIZE,
} from "@/pages/users/constants";
import { UsersSearchParamsSchema } from "@/pages/users/types/users-search.types";
import { extractApiMessage } from "@/pages/users/utils/users.utils";
import { IUser, UserStatusFilter } from "@/types/user.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

type FormDialogState = "create" | IUser | null;

export const Route = createFileRoute(APP_ROUTES_FULL.USERS)({
  component: UsersPage,
  validateSearch: zodValidator(UsersSearchParamsSchema),
  staticData: {
    config: {
      breadcrumb: { title: "users", to: APP_ROUTES_TO.USERS },
      title: "users",
      layout: { scroll: false },
    },
    permissions: [PERMISSIONS.AUTH_SVC_USER_LIST],
  },
});

function UsersPage() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch({ structuralSharing: true });
  const { userData } = useAuth();
  const permissionCodes = userData?.permissionCodes || [];

  const canCreate = permissionCodes.includes(
    PERMISSIONS.AUTH_SVC_AUTH_REGISTER,
  );
  const canUpdate = permissionCodes.includes(PERMISSIONS.AUTH_SVC_USER_UPDATE);
  const canDelete = permissionCodes.includes(PERMISSIONS.AUTH_SVC_USER_DELETE);

  const page = searchParams.page ?? PAGINATION.DEFAULT_PAGE;
  const debouncedSearch = searchParams.search?.trim() ?? "";
  const statusFilter = searchParams.status ?? DEFAULT_USER_STATUS_FILTER;
  const permissionGroupFilter = searchParams.permissionGroupIds ?? [];

  const [searchInput, setSearchInput] = useState(debouncedSearch);
  const [formDialog, setFormDialog] = useState<FormDialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<IUser | null>(null);

  useEffect(() => {
    setSearchInput(debouncedSearch);
  }, [debouncedSearch]);

  const handleDebouncedSearch = useCallback(
    (value: string) => {
      navigate({
        to: Route.to,
        search: (old) => ({
          ...old,
          search: value || undefined,
          page: undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleStatusFilterChange = (values: string[]) => {
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        status: values as UserStatusFilter[],
        page: undefined,
      }),
      replace: true,
    });
  };

  const handlePermissionGroupFilterChange = (values: string[]) => {
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        permissionGroupIds: values.length ? values : undefined,
        page: undefined,
      }),
      replace: true,
    });
  };

  const handlePageChange = (nextPage: number) => {
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        page: nextPage === PAGINATION.DEFAULT_PAGE ? undefined : nextPage,
      }),
      replace: true,
    });
  };

  const clearFilters = () => {
    setSearchInput("");
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        search: undefined,
        status: undefined,
        permissionGroupIds: undefined,
        page: undefined,
      }),
      replace: true,
    });
  };

  const { data, isLoading, isFetching, isError, error, refetch } = useUsers({
    page,
    limit: USERS_PAGE_SIZE,
    search: debouncedSearch,
    status: statusFilter,
    permissionGroupIds: permissionGroupFilter,
  });
  const { data: permissionGroups = [] } = usePermissionGroups();

  const deleteMutation = useDeleteUser();

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, getTotalPages(total, USERS_PAGE_SIZE));
  const hasActiveFilters =
    debouncedSearch.length > 0 ||
    searchParams.status !== undefined ||
    permissionGroupFilter.length > 0;
  const isRefreshing = isFetching && !!data;

  const handleFormSuccess = (action: "created" | "updated") => {
    setFormDialog(null);
    if (action === "created") {
      handlePageChange(PAGINATION.DEFAULT_PAGE);
    }
    toast.success(action === "created" ? t("user_created") : t("user_updated"));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t("user_deleted"));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractApiMessage(err, t("failed_to_delete_user")));
    }
  };

  if (isError) {
    return (
      <ErrorState
        title={t("failed_to_load_users")}
        message={extractApiMessage(error, t("try_again_later"))}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("users")}</h1>
          <p className="text-muted-foreground">
            {isLoading && !data
              ? t("users_page_description")
              : t("users_count", { count: total })}
          </p>
        </div>

        <div className="flex w-full flex-wrap gap-x-2 gap-y-3 sm:w-auto">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => refetch()}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <Spinner size="sm" className="md:mr-2" />
                ) : (
                  <Icon icon={TbRefresh} className="md:mr-2" />
                )}
                <span className="hidden md:inline">{t("refresh")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("refresh")}</TooltipContent>
          </Tooltip>

          {canCreate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={() => setFormDialog("create")}>
                  <Icon icon={TbPlus} className="md:mr-2" />
                  <span className="hidden md:inline">{t("add_user")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("add_user")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onDebouncedChange={handleDebouncedSearch}
          placeholder={t("search_users")}
          className="sm:w-72"
        />

        <MultiSelect
          values={statusFilter}
          onValuesChange={handleStatusFilterChange}
        >
          <MultiSelectTrigger className="flex h-9 min-w-44 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-sm outline-none hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring">
            <MultiSelectValue
              placeholder={t("all_statuses")}
              overflowBehavior="cutoff"
              className="flex-1 text-left"
            />
            <Icon icon={TbChevronDown} className="opacity-50" />
          </MultiSelectTrigger>
          <MultiSelectContent>
            {USER_STATUS_FILTER_OPTIONS.map((status) => (
              <MultiSelectItem key={status} value={status}>
                {t(status)}
              </MultiSelectItem>
            ))}
          </MultiSelectContent>
        </MultiSelect>

        <MultiSelect
          values={permissionGroupFilter}
          onValuesChange={handlePermissionGroupFilterChange}
        >
          <MultiSelectTrigger className="flex h-9 min-w-52 max-w-96 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-sm outline-none hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring">
            <MultiSelectValue
              placeholder={t("all_permission_groups")}
              overflowBehavior="cutoff"
              className="flex-1 text-left"
            />
            <Icon icon={TbChevronDown} className="opacity-50" />
          </MultiSelectTrigger>
          <MultiSelectContent>
            {permissionGroups.map((group) => (
              <MultiSelectItem
                key={group.id}
                value={group.id}
                badgeLabel={group.name}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="size-2.5 shrink-0 rounded-full border border-border"
                    style={{ backgroundColor: group.color }}
                  />
                  {group.name}
                </span>
              </MultiSelectItem>
            ))}
          </MultiSelectContent>
        </MultiSelect>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <Icon icon={TbX} className="mr-1.5" />
            {t("clear_filters")}
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {!isLoading && users.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <EmptyState
              variant="simple"
              title={hasActiveFilters ? t("no_matches") : t("no_users_found")}
              description={
                hasActiveFilters
                  ? t("try_different_search")
                  : t("create_your_first_user")
              }
            />
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                <Icon icon={TbX} className="mr-2" />
                {t("clear_filters")}
              </Button>
            ) : (
              canCreate && (
                <Button onClick={() => setFormDialog("create")}>
                  <Icon icon={TbPlus} className="mr-2" />
                  {t("add_user")}
                </Button>
              )
            )}
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-hidden">
              <div className="hidden h-full md:block">
                <UsersTable
                  users={users}
                  isLoading={isLoading && !data}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  onEdit={setFormDialog}
                  onDelete={setDeleteTarget}
                />
              </div>
              <div className="h-full space-y-3 overflow-auto md:hidden">
                {isLoading && !data
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-36 animate-pulse rounded-xl border border-border bg-muted/40"
                      />
                    ))
                  : users.map((user) => (
                      <UserCard
                        key={user.id}
                        user={user}
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        onEdit={setFormDialog}
                        onDelete={setDeleteTarget}
                      />
                    ))}
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-muted/30 px-1 py-2 sm:px-2">
                <p className="text-xs text-muted-foreground">
                  {t("showing_results", {
                    start: (page - 1) * USERS_PAGE_SIZE + 1,
                    end: Math.min(page * USERS_PAGE_SIZE, total),
                    total,
                  })}
                </p>
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isLoading={isFetching}
                />
              </div>
            )}
          </>
        )}
      </div>

      <UserFormDialog
        open={formDialog !== null}
        user={formDialog === "create" ? null : formDialog}
        onClose={() => setFormDialog(null)}
        onSuccess={handleFormSuccess}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_user")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_user_confirmation", {
                name: deleteTarget?.name || deleteTarget?.email,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t("deleting") : t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
